import { yupResolver } from '@hookform/resolvers/yup';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Control,
  SubmitErrorHandler,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ProductionMasters,
  TossMaterial,
  TossMaterialCategoryTree,
  TossMaterialGroup,
  TossMaterialMaterialName,
  TossMaterialSendingMaterial,
  TossMaterialShareDestination,
} from '../../../../../api/model';
import yup from '../../../../../utils/custom-yup';
import { CheckBox } from '../../../../atoms/CheckBox';
import DatePicker from '../../../../atoms/DatePicker';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import SingleSelect from '../../../../atoms/SingleSelect';
import SlideToggle from '../../../../modules/SlideToggle';
import { DroppableTd, InnerTabCommonNavi } from './CommonComponents';

interface Master0TrProps {
  productionId: number; // 制作管理 ID
  i: number; // トス資料のインデックス 1
  j: number; // トス資料のインデックス 2
  tossMaterial: TossMaterial; // 表示対象のトス資料
  tossMaterialSendingMaterials: TossMaterialSendingMaterial[]; // 原作送付目安マスタデータ
  tossMaterialMaterialNames: TossMaterialMaterialName[]; // 資料名マスタデータ
  shareDestinationProductions: TossMaterialShareDestination[]; // 共有先
  editable: boolean; // 編集モードかどうか
  searchWord: string; // 資料名検索ボックスに入力された値
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す setValue
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す getValues
  register: UseFormRegister<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す register
  control: Control<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す control
  replace: UseFieldArrayReplace<
    {
      toss_materials: TossMaterial[][];
    },
    `toss_materials.${number}`
  >; // useFieldArrya の返す replace
  remove: UseFieldArrayRemove; // useFieldArray の返す remove
}

/**
 * 0マスタタブの各トス資料に関する行
 * @param param0
 * @returns
 */
function Master0Tr({
  productionId,
  i,
  j,
  tossMaterial,
  tossMaterialSendingMaterials,
  tossMaterialMaterialNames,
  shareDestinationProductions,
  editable,
  searchWord,
  setValue,
  getValues,
  register,
  control,
  replace,
  remove,
}: Master0TrProps) {
  // コメントの値をリアルタイムに取得
  const comment = useWatch({ control, name: `toss_materials.${i}.${j}.comment` });

  // file1 の値をリアルタイムに取得
  const file1 = useWatch({ control, name: `toss_materials.${i}.${j}.file1` });

  // 行を表示するかどうか
  const hidden = useMemo(() => {
    if (searchWord.length === 0) {
      return false;
    }
    return file1?.file_name.includes(searchWord) !== true;
  }, [file1?.file_name, searchWord]);

  return (
    <tr className={hidden ? 'hidden-item' : undefined}>
      <td>
        {/* 原作送付目安 */}
        <SingleSelect
          name={`toss_materials.${i}.${j}.toss_material_sending_material_id`}
          allowEmpty={false}
          options={tossMaterialSendingMaterials}
          getValue={(z) => z.toss_material_sending_material_id}
          getLabel={(z) => z.sending_material_name}
          register={register}
          isDisabled={!editable}
        />
      </td>
      {/* 資料資料名ID */}
      <td>
        <SingleSelect
          name={`toss_materials.${i}.${j}.toss_material_material_name_id`}
          allowEmpty={false}
          options={tossMaterialMaterialNames}
          getValue={(z) => z.toss_material_material_name_id}
          getLabel={(z) => z.material_name}
          register={register}
          isDisabled={!editable}
        />
      </td>
      {/* コメント */}
      <td>
        {/* 5文字ごとに改行を入れる */}
        {(comment ?? '').split('').map((z, k) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={k}>
            {k > 0 && k % 20 === 0 ? <br /> : null}
            {z}
          </React.Fragment>
        ))}
      </td>
      <td>
        <DatePicker name={`toss_materials.${i}.${j}.issued_at`} isDisabled={!editable} control={control} displayMark />
      </td>
      {/* 共有先 */}
      {shareDestinationProductions.map((z, k) => (
        <td key={z.production_id}>
          <CheckBox
            label={undefined}
            name={`toss_materials.${i}.${j}.share_destination_productions.${k}`}
            isDisabled={!editable}
            value={z.production_id}
            register={register}
          />
        </td>
      ))}
      {/* トス資料ファイル1 */}
      <DroppableTd
        productionId={productionId}
        tabIndex={1 as number}
        editable={editable}
        file={file1}
        comment={comment}
        getPresentFilenames={() => {
          const values = getValues();
          const filenames = values.toss_materials
            .flat()
            .flat()
            .flatMap((x) => {
              if (x.file1?.file_name != null && x.file2?.file_name != null) {
                return [x.file1?.file_name, x.file2?.file_name];
              }
              if (x.file1?.file_name != null) {
                return x.file1?.file_name;
              }
              if (x.file2?.file_name != null) {
                return x.file2?.file_name;
              }
              return [];
            });
          return filenames;
        }}
        onDropSucceeded={(files) => {
          // 現在表示されているトス資料の情報
          const prevTossMaterials = getValues().toss_materials[i];

          // 次に設定するトス資料情報
          const nextTossMaterials: TossMaterial[] = [];

          if (file1 != null) {
            // もともとその行にファイルが設定してあった場合

            // そのまま表示するデータ
            for (let k = 0; k <= j; k += 1) {
              nextTossMaterials.push(prevTossMaterials[k]);
            }

            // アップロードされたファイルを順番に表示する
            files.forEach((z) => {
              const newTossMaterial: TossMaterial = {
                ...prevTossMaterials[j],
              };
              newTossMaterial.file1 = z;

              // ファイルがアップロードされた行以外は ID、最終更新日、更新者、初期データフラグをリセット
              newTossMaterial.toss_material_id = undefined;
              newTossMaterial.updated_on = undefined;
              newTossMaterial.update_user_name = undefined;
              newTossMaterial.is_initial = false;

              nextTossMaterials.push(newTossMaterial);
            });

            // 下にずらして表示するデータ
            for (let k = j + 1; k < prevTossMaterials.length; k += 1) {
              nextTossMaterials.push(prevTossMaterials[k]);
            }
          } else {
            // もともとその行にはファイルが設定していなかった場合

            // そのまま表示するデータ
            for (let k = 0; k < j; k += 1) {
              nextTossMaterials.push(prevTossMaterials[k]);
            }

            // アップロードされたファイルを順番に表示する
            files.forEach((z, k) => {
              const newTossMaterial: TossMaterial = {
                ...prevTossMaterials[j],
              };
              newTossMaterial.file1 = z;
              if (k !== 0) {
                // ファイルがアップロードされた行以外は ID、最終更新日、更新者、初期データフラグをリセット
                newTossMaterial.toss_material_id = undefined;
                newTossMaterial.updated_on = undefined;
                newTossMaterial.update_user_name = undefined;
                newTossMaterial.is_initial = false;
              }
              nextTossMaterials.push(newTossMaterial);
            });

            // 下にずらして表示するデータ
            for (let k = j + 1; k < prevTossMaterials.length; k += 1) {
              nextTossMaterials.push(prevTossMaterials[k]);
            }
          }

          replace(nextTossMaterials);
        }}
        onChangeComment={(next) => setValue(`toss_materials.${i}.${j}.comment`, next)}
        onChangeMaterialFileName={(next) => setValue(`toss_materials.${i}.${j}.file1.file_name`, next ?? '')}
        onClickDelete={() => {
          // ファイルを空に
          setValue(`toss_materials.${i}.${j}.file1`, undefined);
        }}
        onCickDeleteRow={tossMaterial.is_initial === true || file1 != null ? undefined : () => remove(j)}
      />
      {/* 最終更新日 */}
      <td>{tossMaterial.updated_on?.replaceAll('-', '/')}</td>
      {/* 最終更新者名 */}
      <td>{tossMaterial.update_user_name}</td>
    </tr>
  );
}

interface Material0TableProps {
  productionId: number; // 制作管理 ID
  productionCategoryId: number; // 制作管理カテゴリ ID
  tossMaterialGroup: TossMaterialGroup; // 表示対象のトス資料グループ
  i: number; // トス資料グループのインデックス
  editable: boolean; // 編集モードかどうか
  tossMaterialSendingMaterials: TossMaterialSendingMaterial[]; // 原作送付目安マスタデータ
  tossMaterialMaterialNames: TossMaterialMaterialName[]; // 資料名マスタデータ
  shareDestinationProductions: TossMaterialShareDestination[]; // 共有先
  open: boolean; // 開いているかどうか
  toggleOpen: () => void; // 開閉操作
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す setValue
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す getValues
  register: UseFormRegister<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す register
  control: Control<{
    toss_materials: TossMaterial[][];
  }>; // useForm の返す control
}

/**
 * 0マスタタブのトス資料の表
 * @param param0
 * @returns
 */
function Material0Table({
  i,
  productionId,
  productionCategoryId,
  editable,
  tossMaterialSendingMaterials,
  tossMaterialMaterialNames,
  shareDestinationProductions,
  tossMaterialGroup,
  open,
  toggleOpen,
  setValue,
  getValues,
  register,
  control,
}: Material0TableProps) {
  // フォームが配列になる場合に用いる
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `toss_materials.${i}`,
  });

  // 言語
  const { t } = useTranslation();

  // 資料名検索
  const {
    control: searchWordControl,
    register: registerSearchWord,
    setValue: setValueSearchWord,
  } = useForm({
    defaultValues: { 'search-word': '' },
  });
  const searchWord = useWatch({ control: searchWordControl, name: 'search-word' });

  // モードが変わった時は資料名検索テキストボックスは空にする
  useEffect(() => {
    setValueSearchWord('search-word', '');
  }, [editable, setValueSearchWord]);

  if (tossMaterialGroup.toss_material_group_id == null || productionCategoryId == null) {
    return null;
  }

  return (
    <>
      <h3 className="mdh2">
        {/* トス資料グループ名 */}
        <span>
          {tossMaterialGroup.group_name}
          {/* 表示しているトス資料の件数 */}
          <span className="num">{fields.length}</span>
          <button
            type="button"
            className="text-button"
            onClick={() => {
              // ▽や△を押すことで、詳細部分を広げたり閉じたりする
              toggleOpen();
            }}
          >
            <i
              className={`fa-solid fa-chevron-${open ? 'down' : 'up'}`} // △と▽を切り替える
            />
          </button>
        </span>
      </h3>
      {/* 開いたり閉じたりできるエリア */}
      <SlideToggle open={open}>
        <div className="masterfr2">
          {/* 新規追加ボタン */}
          <p className="newadd">
            <button
              type="button"
              className="text-button"
              onClick={() => {
                if (!editable) return;

                // 新規追加した行の初期値を設定する
                const newTossMaterial: TossMaterial = {
                  toss_material_id: undefined,
                  toss_material_production_category_id: productionCategoryId,
                  toss_material_group_id: tossMaterialGroup.toss_material_group_id,
                  toss_material_subgroup_id: undefined,
                  toss_material_sending_material_id:
                    tossMaterialSendingMaterials.length > 0
                      ? tossMaterialSendingMaterials[0].toss_material_sending_material_id
                      : undefined,
                  toss_material_material_name_id:
                    tossMaterialMaterialNames.length > 0
                      ? tossMaterialMaterialNames[0].toss_material_material_name_id
                      : undefined,
                  check: false,
                  toss_material_target_manual_id: undefined,
                  sp: undefined,
                  rev: undefined,
                  toss_material_schedule_id: undefined,
                  comment: undefined,
                  is_initial: false,
                  sort_order: 0,
                  file1: undefined,
                  file2: undefined,
                  share_destination_productions: [],
                  update_user_name: undefined,
                  updated_on: undefined,
                  issued_at: undefined,
                };
                append(newTossMaterial);
              }}
              disabled={!editable}
            >
              <i className="fa-solid fa-circle-plus" />
              {t('新規追加')}
            </button>
          </p>
          {/* トス資料の表 */}
          <div className="scroll">
            <table className="table10">
              <tbody>
                <tr>
                  <th>{t('原作送付目安')}</th>
                  <th>{t('資料名')}</th>
                  <th>{t('コメント')}</th>
                  <th className="data">{t('date')}</th>
                  {shareDestinationProductions.map((y) => (
                    <th key={y.production_id} className="shared-production-th">
                      <div className="shared-production-th-inner-div">
                        {/* 5文字ごとに改行を入れる */}
                        {(y.production_no ?? '').split('').map((z, k) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <React.Fragment key={k}>
                            {k > 0 && k % 5 === 0 ? <br /> : null}
                            {z}
                          </React.Fragment>
                        ))}
                        <br />
                        <div className="shared-production-badge">
                          {(y.detail ?? '').split('').map((z, k) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <React.Fragment key={k}>
                              {k > 0 && k % 5 === 0 ? <br /> : null}
                              {z}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </th>
                  ))}
                  <th>
                    {t('資料')}
                    <span className="search_box">
                      <input type="text" {...registerSearchWord('search-word')} disabled={editable} />
                    </span>
                  </th>
                  <th>{t('最終更新日')}</th>
                  <th>{t('更新者')}</th>
                </tr>
                {fields.map((y, j) => (
                  <Master0Tr
                    key={y.id}
                    productionId={productionId}
                    i={i}
                    j={j}
                    tossMaterial={y}
                    tossMaterialSendingMaterials={tossMaterialSendingMaterials}
                    tossMaterialMaterialNames={tossMaterialMaterialNames}
                    shareDestinationProductions={shareDestinationProductions}
                    editable={editable}
                    searchWord={searchWord}
                    setValue={setValue}
                    getValues={getValues}
                    control={control}
                    register={register}
                    replace={replace}
                    remove={remove}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SlideToggle>
    </>
  );
}

/**
 * トス資料ツリーからフォームの初期値を作成
 * @param tree
 * @returns
 */
const getDefaultValues = (
  tree: TossMaterialCategoryTree,
  shareDestinationProductions: TossMaterialShareDestination[]
): { toss_materials: TossMaterial[][] } => {
  const result: TossMaterial[][] = [];
  (tree.toss_material_groups ?? []).forEach((x) => {
    result.push(x.toss_materials ?? []);
  });
  for (let i = 0; i < result.length; i += 1) {
    for (let j = 0; j < result[i].length; j += 1) {
      result[i][j].share_destination_productions = shareDestinationProductions.map((x) =>
        x.production_id != null && result[i][j].share_destination_productions?.includes(x.production_id)
          ? x.production_id
          : undefined
      ) as number[]; // 強制的にキャスト（これがないとチェックボックスが全て左に詰められてしまう）
    }
  }
  return { toss_materials: result };
};

interface Master0TabProps {
  productionId: number; // 制作管理 ID
  tree: TossMaterialCategoryTree; // トス資料ツリー
  shareDestinationProductions: TossMaterialShareDestination[]; // トス資料共有先
  masters: ProductionMasters; // 制作管理タブ用マスターデータ
  editable: boolean; // 編集状態かどうか
  save: (tossMaterialList: TossMaterial[]) => Promise<void>; // 保存ボタン押下時の動作
  edit: () => Promise<void>; // 編集ボタン押下時の動作
  cancel: () => Promise<void>; // キャンセルボタン押下時の動作
  setDisplayValidationError: (next: boolean) => void; // バリデーションエラーの表示
}

export interface Master0TabHandler {
  validate: () => Promise<boolean>; // バリデーションを実施
  submit: () => Promise<void>; // フォームを送信
  refresh: () => void; // フォームをリフレッシュ
}

/**
 * トス資料タブの 0マスタタブ
 */
const Master0Tab = forwardRef<Master0TabHandler, Master0TabProps>(
  (
    {
      productionId,
      tree,
      shareDestinationProductions,
      masters,
      editable,
      save,
      edit,
      cancel,
      setDisplayValidationError,
    },
    ref
  ) => {
    // バリデーション用のスキーマ
    const schema = yup.object().shape({
      toss_materials: yup
        .array()
        .required()
        .of(
          yup
            .array()
            .required()
            .of(
              yup.object().shape({
                toss_material_id: yup
                  .number()
                  .nullable()
                  .transform((v) => (Number.isNaN(Number(v)) ? null : Number(v)))
                  .default(null),
                toss_material_production_category_id: yup.number().required(),
                toss_material_group_id: yup.number().required(),
                toss_material_subgroup_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                toss_material_sending_material_id: yup
                  .number()
                  .nullable()
                  .transform((v) => (v === '' ? null : Number(v)))
                  .default(null),
                toss_material_material_name_id: yup
                  .number()
                  .nullable()
                  .transform((v) => (v === '' ? null : Number(v)))
                  .default(null),
                check: yup
                  .boolean()
                  .required()
                  .transform(() => false),
                toss_material_target_manual_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                sp: yup
                  .string()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                rev: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                toss_material_schedule_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                comment: yup.string().nullable().default(null),
                is_initial: yup.boolean().required(),
                sort_order: yup.number().required(),
                file1: yup
                  .object()
                  .nullable()
                  .shape({
                    toss_material_file_id: yup
                      .number()
                      .nullable()
                      .transform((v) => (v || v === 0 ? Number(v) : null)),
                    file_name: yup
                      .string()
                      .nullable()
                      .transform((v) => (v === '' ? null : String(v))),
                  })
                  .default(null),
                file2: yup
                  .object()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                share_destination_productions: yup
                  .array()
                  .required()
                  .of(
                    yup
                      .number()
                      .nullable()
                      .transform((v) => (Number.isNaN(Number(v)) ? null : Number(v)))
                  )
                  .transform((v) =>
                    // 配列に undefined や false が含まれる場合があるのでフィルター
                    Array.isArray(v) ? (v.filter((w) => w && w !== 0) as Array<number>) : new Array<number>()
                  ),
                update_user_name: yup.string().nullable().default(null),
                updated_on: yup.string().date().nullable().default(null),
                issued_at: yup.string().date().nullable().default(null),
              })
            )
        ),
    });

    // フォームを利用できるようにする
    const { register, handleSubmit, setValue, getValues, control, reset } = useForm({
      defaultValues: getDefaultValues(tree, shareDestinationProductions),
      resolver: yupResolver(schema),
    });

    // 詳細部分が開いているグループの ID
    const [openGroupIds, setOpenGroupIds] = useState<Array<number | undefined>>([]);

    // バリデーション
    const validate = useCallback(async () => {
      let result = false as boolean;
      await handleSubmit(() => {
        result = true;
      })();
      return result;
    }, [handleSubmit]);

    // 保存ボタン押下時の動作
    const submit = useCallback(async () => {
      // バリデーション結果が有効だった場合
      const onValid = async (data: { toss_materials: TossMaterial[][] }) => {
        // API 実行
        const tossMaterialList = data.toss_materials.flat();

        await save(tossMaterialList);
      };

      // バリデーション結果が無効だった場合（エラーモーダルを表示）
      const onInvalid: SubmitErrorHandler<{ toss_materials: TossMaterial[][] }> = () => {
        // バリデーションエラーをモーダルで表示
        setDisplayValidationError(true);
      };

      // フォーム送信時の処理を設定
      await handleSubmit(onValid, onInvalid)();
    }, [handleSubmit, save, setDisplayValidationError]);

    const refresh = useCallback(() => {
      reset(getDefaultValues(tree, shareDestinationProductions));
    }, [reset, tree, shareDestinationProductions]);

    // 親コンポーネントから操作できるようにする
    useImperativeHandle(ref, () => ({
      validate,
      submit,
      refresh,
    }));

    // 必要なデータがない場合
    if (
      masters.toss_material_sending_materials == null ||
      masters.toss_material_material_names == null ||
      tree.toss_material_production_category_id == null ||
      tree.toss_material_groups == null
    ) {
      return (
        <div id="tab2-content1" className="tab-content2">
          <NoDataComponent />
        </div>
      );
    }

    return (
      <div id="tab2-content1" className="tab-content2">
        <form>
          {/* 上部のタブ内メニュー */}
          <InnerTabCommonNavi
            productionId={productionId}
            tabIndex={1 as number}
            editable={editable}
            open={() => setOpenGroupIds((tree.toss_material_groups ?? []).map((x) => x.toss_material_group_id))}
            close={() => setOpenGroupIds([])}
            edit={edit}
            cancel={cancel}
          />
          {/* 本体 トス資料グループごとに表を表示 */}
          {(tree.toss_material_groups ?? []).map((x, i) => {
            if (
              tree.toss_material_production_category_id == null ||
              masters.toss_material_sending_materials == null ||
              masters.toss_material_material_names == null
            ) {
              return <React.Fragment key={x.toss_material_group_id} />;
            }

            return (
              <Material0Table
                key={x.toss_material_group_id}
                tossMaterialGroup={x}
                open={openGroupIds.includes(x.toss_material_group_id)}
                productionId={productionId}
                i={i}
                productionCategoryId={tree.toss_material_production_category_id}
                editable={editable}
                tossMaterialSendingMaterials={masters.toss_material_sending_materials.filter(
                  (y) => y.toss_material_group_id === x.toss_material_group_id
                )}
                tossMaterialMaterialNames={masters.toss_material_material_names.filter(
                  (y) => y.toss_material_group_id === x.toss_material_group_id
                )}
                shareDestinationProductions={shareDestinationProductions}
                setValue={setValue}
                getValues={getValues}
                register={register}
                toggleOpen={() => {
                  setOpenGroupIds((preList) => {
                    if (x.toss_material_group_id == null) {
                      return preList;
                    }
                    if (preList.includes(x.toss_material_group_id)) {
                      return preList.filter((y) => y !== x.toss_material_group_id);
                    }
                    return [...preList, x.toss_material_group_id];
                  });
                }}
                control={control}
              />
            );
          })}
        </form>
      </div>
    );
  }
);

export default Master0Tab;
